import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import ApiService from '../../../../../services/ApiService';
import { NcmProfileItemDto } from '../NcmRules/types';
import {
  NcmBuscaInput, NcmBuscaWrapper, NcmDropdown, NcmField, NcmFiltroInput, NcmGrupoTitulo, NcmGrupoVazio,
  NcmIpiChip, NcmMoreButton, NcmOpcao, NcmOpcaoConflito, NcmOpcaoDescricao, NcmRemover, NcmSemResultado,
  NcmSeparador, NcmTag,
} from './style';

export interface NcmSelecionado {
  id: number;
  name: string;
}

export interface NcmIpiChipInfo {
  tone: 'ok' | 'mute' | 'bad';
  label: string;
  clickable: boolean;
}

export interface NcmConflito {
  taxConfigId: number;
  nome: string | null;
}

interface Props {
  selecionados: NcmSelecionado[];
  itensEmpresa: NcmProfileItemDto[];
  conflitos: Record<number, NcmConflito>;
  ipiChipDe: (ncmId: number) => NcmIpiChipInfo;
  ipiNaoIncide: boolean;
  onAdicionar: (ncm: NcmSelecionado) => void;
  onRemover: (id: number) => void;
  onAbrirRegras: () => void;
}

interface OpcaoNcm {
  id: number;
  ncm: string;
  descricao: string | null;
  conflito: NcmConflito | null;
}

interface ResultadoCatalogo {
  id: number;
  ncm: string;
  descricao: string | null;
}

const PREVIA_CHIPS = 8;
const BUSCA_ATRASO_MS = 300;
const CATALOGO_LIMITE = 20;
const DESTAQUE_MS = 4000;

const ehResultado = (v: unknown): v is ResultadoCatalogo =>
  typeof v === 'object' && v !== null && 'id' in v && typeof v.id === 'number' && 'ncm' in v && typeof v.ncm === 'string' &&
  (!('descricao' in v) || v.descricao === null || typeof v.descricao === 'string');

const digitos = (v: string) => v.replace(/\D/g, '');

const combina = (termo: string, ncm: string, descricao: string | null): boolean => {
  const d = digitos(termo);
  if (d) return digitos(ncm).startsWith(d);
  const t = termo.trim().toLowerCase();
  return !t || (descricao ?? '').toLowerCase().includes(t);
};

export default function NcmNaturezaCampo({
  selecionados, itensEmpresa, conflitos, ipiChipDe, ipiNaoIncide, onAdicionar, onRemover, onAbrirRegras,
}: Props): React.ReactElement {
  const [termo, setTermo] = useState('');
  const [aberto, setAberto] = useState(false);
  const [expandido, setExpandido] = useState(false);
  const [filtro, setFiltro] = useState('');
  const [novoId, setNovoId] = useState<number | null>(null);
  const [catalogo, setCatalogo] = useState<ResultadoCatalogo[]>([]);
  const [termoResposta, setTermoResposta] = useState('');
  const busca = termo.trim();
  const catalogoAtivo = digitos(busca).length >= 2 || busca.length >= 3;

  useEffect(() => {
    if (!catalogoAtivo) {
      setCatalogo([]);
      setTermoResposta('');
      return;
    }
    let ativo = true;
    const timer = setTimeout(() => {
      ApiService.stockLookup
        .searchNcmCatalogo(busca, CATALOGO_LIMITE, 0)
        .then((res: { data?: unknown }) => {
          if (!ativo) return;
          setCatalogo((Array.isArray(res?.data) ? res.data : []).filter(ehResultado));
          setTermoResposta(busca);
        })
        .catch(() => {
          if (!ativo) return;
          setCatalogo([]);
          setTermoResposta(busca);
        });
    }, BUSCA_ATRASO_MS);
    return () => {
      ativo = false;
      clearTimeout(timer);
    };
  }, [busca, catalogoAtivo]);

  useEffect(() => {
    if (novoId === null) return;
    const timer = setTimeout(() => setNovoId(null), DESTAQUE_MS);
    return () => clearTimeout(timer);
  }, [novoId]);

  const idsSelecionados = useMemo(() => new Set(selecionados.map((s) => s.id)), [selecionados]);

  const daEmpresa = useMemo<OpcaoNcm[]>(
    () =>
      itensEmpresa
        .filter((i) => !idsSelecionados.has(i.ncmId) && combina(busca, i.ncm, i.descricao))
        .map((i) => ({ id: i.ncmId, ncm: i.ncm, descricao: i.descricao, conflito: conflitos[i.ncmId] ?? null })),
    [itensEmpresa, idsSelecionados, busca, conflitos],
  );

  const doCatalogo = useMemo<OpcaoNcm[]>(() => {
    const idsEmpresa = new Set(itensEmpresa.map((i) => i.ncmId));
    return catalogo
      .filter((c) => !idsSelecionados.has(c.id) && !idsEmpresa.has(c.id))
      .map((c) => ({ id: c.id, ncm: c.ncm, descricao: c.descricao, conflito: conflitos[c.id] ?? null }));
  }, [catalogo, itensEmpresa, idsSelecionados, conflitos]);

  const adicionar = (o: OpcaoNcm) => {
    if (o.conflito || idsSelecionados.has(o.id)) return;
    onAdicionar({ id: o.id, name: o.ncm });
    setNovoId(o.id);
    setTermo('');
    setAberto(false);
  };

  const adicionarDoEnter = () => {
    const habilitadas = [...daEmpresa, ...doCatalogo].filter((o) => !o.conflito);
    if (busca.length >= 2 && busca === termoResposta) {
      const primeira = habilitadas[0];
      if (primeira) adicionar(primeira);
      return;
    }
    const d = digitos(busca);
    if (!d) return;
    const exata = habilitadas.find((o) => digitos(o.ncm) === d);
    if (exata) adicionar(exata);
  };

  const recentesPrimeiro = useMemo(() => [...selecionados].reverse(), [selecionados]);
  const filtrados = useMemo(
    () => (expandido && digitos(filtro) ? recentesPrimeiro.filter((s) => digitos(s.name).includes(digitos(filtro))) : recentesPrimeiro),
    [expandido, filtro, recentesPrimeiro],
  );
  const visiveis = expandido ? filtrados : filtrados.slice(0, PREVIA_CHIPS);

  const alternarExpandido = () => {
    setExpandido((v) => !v);
    setFiltro('');
  };

  const linha = (o: OpcaoNcm) => {
    const chip = ipiChipDe(o.id);
    return (
      <NcmOpcao
        key={o.id}
        $desabilitada={!!o.conflito}
        aria-disabled={!!o.conflito}
        onClick={() => adicionar(o)}
      >
        <b>{o.ncm}</b>
        <NcmIpiChip $tone={chip.tone}>{chip.label}</NcmIpiChip>
        <NcmOpcaoDescricao>{o.descricao ?? ''}</NcmOpcaoDescricao>
        {o.conflito && <NcmOpcaoConflito>em {o.conflito.nome ?? 'outra natureza'}</NcmOpcaoConflito>}
      </NcmOpcao>
    );
  };

  const linhaCatalogo = (o: OpcaoNcm) => (
    <NcmOpcao key={o.id} $desabilitada={!!o.conflito} aria-disabled={!!o.conflito} onClick={() => adicionar(o)}>
      <b>{o.ncm}</b>
      <NcmOpcaoDescricao>{o.descricao ?? ''}</NcmOpcaoDescricao>
      {o.conflito && <NcmOpcaoConflito>em {o.conflito.nome ?? 'outra natureza'}</NcmOpcaoConflito>}
    </NcmOpcao>
  );

  return (
    <>
      <NcmBuscaWrapper>
        <NcmBuscaInput
          type="text"
          value={termo}
          placeholder="Adicionar NCM…"
          autoComplete="off"
          onChange={(e) => {
            setTermo(e.target.value);
            setAberto(true);
          }}
          onFocus={() => setAberto(true)}
          onBlur={() => setTimeout(() => setAberto(false), 200)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              adicionarDoEnter();
            }
          }}
        />
        {aberto && (
          <NcmDropdown>
            <NcmGrupoTitulo>Da empresa, fora desta natureza</NcmGrupoTitulo>
            {daEmpresa.length > 0 ? daEmpresa.map(linha) : <NcmGrupoVazio>Nenhum NCM da empresa com esse código.</NcmGrupoVazio>}
            <NcmSeparador />
            <NcmGrupoTitulo>Catálogo oficial</NcmGrupoTitulo>
            {doCatalogo.length > 0 ? (
              doCatalogo.map(linhaCatalogo)
            ) : (
              <NcmGrupoVazio>
                {catalogoAtivo ? 'Nada encontrado no catálogo.' : 'Digite 2 ou mais dígitos para buscar no catálogo.'}
              </NcmGrupoVazio>
            )}
          </NcmDropdown>
        )}
      </NcmBuscaWrapper>

      <NcmField>
        {expandido && (
          <NcmFiltroInput
            type="text"
            value={filtro}
            placeholder="Filtrar nesta natureza…"
            autoComplete="off"
            onChange={(e) => setFiltro(e.target.value)}
          />
        )}
        {visiveis.map((ncm) => {
          const chip = ipiChipDe(ncm.id);
          return (
            <NcmTag key={ncm.id} $novo={ncm.id === novoId}>
              {ncm.name}
              {!ipiNaoIncide && (
                <NcmIpiChip
                  $tone={chip.tone}
                  title={chip.clickable ? 'Cadastrar regra por NCM' : undefined}
                  onClick={chip.clickable ? onAbrirRegras : undefined}
                >
                  {chip.label}
                </NcmIpiChip>
              )}
              <NcmRemover type="button" title="Remover" onClick={() => onRemover(ncm.id)}>
                <X size={14} />
              </NcmRemover>
            </NcmTag>
          );
        })}
        {expandido && visiveis.length === 0 && <NcmSemResultado>Nenhum NCM com esse filtro.</NcmSemResultado>}
        {selecionados.length > PREVIA_CHIPS && (
          <NcmMoreButton type="button" onClick={alternarExpandido}>
            {expandido ? 'Mostrar menos' : `+${selecionados.length - PREVIA_CHIPS} NCMs`}
          </NcmMoreButton>
        )}
      </NcmField>
    </>
  );
}
